import { graphql, useFragment } from "react-relay";
import { HostCardFragment$key } from "./__generated__/HostCardFragment.graphql";
import EntityProfilePic from "./EntityProfilePic";
import { FormattedNumber, FormattedPlural } from "react-intl";
import { LinkExternal01Icon } from "@/icons";
import { Link } from "react-router";
import { generatePath } from "react-router";

const HostCardFragment = graphql`
  fragment HostCardFragment on Entity {
    displayName
    username
    ...EntityProfilePicFragment @arguments(thumbnail: true)
  }
`;

type Activity = "EVENT" | "COMPETITION";

export interface HostCardProps {
  host: HostCardFragment$key;
  activity: Activity;
  count?: number;
}

function ActivityPluralLabel({
  activity,
  count,
}: Readonly<Omit<HostCardProps, "host">>) {
  const value = count ?? 0;

  const labels: Record<Activity, { one: string; other: string }> = {
    EVENT: { one: "Challenge: ", other: "Challenges: " },
    COMPETITION: { one: "Track: ", other: "Tracks: " },
  };

  return (
    <FormattedPlural
      value={value}
      one={labels[activity].one}
      other={labels[activity].other}
    >
      {(pluralLabel) => (
        <>
          <span className="text-grey-700 text-xs font-normal leading-4">
            {pluralLabel}
          </span>
          <span className="text-grey-700 text-xs font-bold leading-4">
            <FormattedNumber value={value} notation="compact" />
          </span>
        </>
      )}
    </FormattedPlural>
  );
}

export function HostCard(props: HostCardProps) {
  const host = useFragment(HostCardFragment, props.host);

  return (
    <Link
      to={generatePath("/:username", { username: host.username })}
      className="group w-full max-w-96"
    >
      <div className="w-full px-3 py-2 group-active:bg-grey-100 bg-linear-to-b from-white to-grey-50 rounded-xl outline-1 -outline-offset-1 outline-grey-200 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#A99CF3] focus-visible:shadow-[0px_0px_0px_4px_rgba(169,156,243,1.00)] inline-flex justify-start items-center gap-2 overflow-hidden">
        <div className="flex-1 flex justify-start items-center gap-2">
          <EntityProfilePic entity={host} size="8" />
          <div className="inline-flex flex-col justify-center items-start">
            <div className="flex flex-col justify-start items-start">
              <p className="justify-start text-grey-900 group-hover:text-brand-500 group-active:text-brand-600 text-sm font-semibold leading-5">
                {host.displayName}
              </p>
            </div>
            <div className="justify-start">
              <ActivityPluralLabel {...props} />
            </div>
          </div>
        </div>
        <LinkExternal01Icon className="w-5 h-5 outline-grey-600 text-grey-600 group-hover:text-brand-500 group-hover:outline-brand-500 group-active:text-brand-600 group-active:outline-brand-600" />
      </div>
    </Link>
  );
}
